import LazyLoad from 'vanilla-lazyload';
import lightGallery from 'lightgallery';
import lgVideo from 'lightgallery/plugins/video';
import { initAcfMaps } from './map';

let myLazyLoad;

document.addEventListener('DOMContentLoaded', function () {
    myLazyLoad = new LazyLoad({
        callback_loaded: (el) => {
            const container = el?.closest?.('.has-image-preloader');
            if (container) container.classList.add('is-loaded');
        },
        callback_error: (el) => {
            const container = el?.closest?.('.has-image-preloader');
            if (container) container.classList.add('is-loaded');
        },
    });

    // Only initializes if `.acf-map` is present (and Google Maps is loaded).
    initAcfMaps();
});

function setupImageGridInlineVideos() {
    document.querySelectorAll('.image-grid-block__vimeo').forEach((iframe) => {
        if (iframe.dataset.readyHandlerAttached === 'true') return;
        iframe.dataset.readyHandlerAttached = 'true';

        iframe.addEventListener('load', () => {
            iframe.closest('.image-grid-block__image')?.classList.add('is-video-frame-ready');
        });
    });
}

function setupImageGridInlineVideoHoverPause() {
    document.querySelectorAll('.image-grid-block__image--video').forEach((tile) => {
        if (tile.dataset.hoverPauseReady === 'true') return;
        tile.dataset.hoverPauseReady = 'true';

        const iframe = tile.querySelector('.image-grid-block__vimeo');
        if (!iframe) return;

        const post = (method) => {
            try {
                iframe.contentWindow?.postMessage(JSON.stringify({ method }), '*');
            } catch (_) {}
        };

        tile.addEventListener('mouseenter', () => post('pause'));
        tile.addEventListener('mouseleave', () => post('play'));
    });
}

function setupMenu() {
    // Add hover color change functionality for menu items
    const menuItems = document.querySelectorAll('.menu-item');
    // Change to querySelector since we only need one element
    const taglineSpan = document.querySelector('.tagline-span-text');
    const primaryNav = document.querySelector('.primary-nav');
    const mainNav = document.querySelector('.main-nav');

    menuItems.forEach((item) => {
        const colorClass = Array.from(item.classList).find((cls) => cls.startsWith('color-'));

        item.addEventListener('mouseenter', () => {
            if (colorClass) {
                const colorName = colorClass.replace('color-', '');
                const color = getComputedStyle(document.documentElement).getPropertyValue(`--color-${colorName}`);
                document.documentElement.style.setProperty('--color-highlight', color);
            }

            if (taglineSpan) {
                taglineSpan.classList.add('show');
            } else {
            }
        });

        item.addEventListener('mouseleave', () => {
            document.documentElement.style.setProperty('--color-highlight', '');

            // Only remove the show class if we found the tagline span
            if (taglineSpan) {
                taglineSpan.classList.remove('show');
            }
        });
    });

    const hamburger = document.querySelector('.hamburger');
    hamburger.addEventListener('click', () => {
        hamburger.classList.toggle('is-active');
        document.body.classList.toggle('menu-open');
        const isExpanded = hamburger.getAttribute('aria-expanded') === 'true';
        hamburger.setAttribute('aria-expanded', !isExpanded);

        // Reset submenu state when closing menu
        if (!document.body.classList.contains('menu-open')) {
            closeAllSubmenus();
        }
    });

    // Mobile submenu functionality
    function isMobile() {
        return window.innerWidth < 1020;
    }

    const submenuOriginalParents = new Map();

    function openSubmenu(submenu, parentItem) {
        if (!isMobile()) return;

        // Store original parent if not already stored
        if (!submenuOriginalParents.has(submenu)) {
            submenuOriginalParents.set(submenu, submenu.parentElement);
        }

        // Move submenu to main-nav so it doesn't slide with primary-nav
        if (submenu.parentElement !== mainNav && mainNav) {
            mainNav.appendChild(submenu);
        }

        // Force reflow to ensure transition works
        void submenu.offsetWidth;

        // Slide main menu left first
        if (mainNav) {
            mainNav.classList.add('has-open-submenu');
        }

        // Then slide in submenu after a tiny delay to ensure smooth animation
        requestAnimationFrame(() => {
            submenu.classList.add('is-open');
        });
    }

    function closeSubmenu(submenu) {
        if (!isMobile()) return;

        submenu.classList.remove('is-open');
        if (mainNav) {
            mainNav.classList.remove('has-open-submenu');
        }

        // Wait for transform transition to complete before moving submenu back to original parent
        // This prevents the "drop down" effect during the slide-out animation
        const handleTransitionEnd = (e) => {
            // Only proceed if this is the transform transition
            if (e.propertyName === 'transform') {
                const originalParent = submenuOriginalParents.get(submenu);
                if (originalParent && submenu.parentElement === mainNav) {
                    originalParent.appendChild(submenu);
                }
                submenu.removeEventListener('transitionend', handleTransitionEnd);
            }
        };

        submenu.addEventListener('transitionend', handleTransitionEnd);

        // Fallback timeout in case transitionend doesn't fire
        setTimeout(() => {
            const originalParent = submenuOriginalParents.get(submenu);
            if (originalParent && submenu.parentElement === mainNav) {
                originalParent.appendChild(submenu);
                submenu.removeEventListener('transitionend', handleTransitionEnd);
            }
        }, 350); // Slightly longer than the 0.3s transition
    }

    mainNav?.addEventListener('click', (e) => {
        const back = e.target.closest('.sub-menu-back');
        if (!back || !isMobile()) return;
        e.preventDefault();
        const submenu = back.closest('.sub-menu');
        if (submenu) {
            closeSubmenu(submenu);
        }
    });

    function closeAllSubmenus() {
        // Find all open submenus (they might be in body or in nav)
        const openSubmenus = document.querySelectorAll('.sub-menu.is-open');
        openSubmenus?.forEach((submenu) => {
            closeSubmenu(submenu);
        });
    }

    // Handle clicks on menu items with children (mobile and desktop)
    menuItems.forEach((item) => {
        const submenu = item.querySelector('.sub-menu');
        if (!submenu) return;

        const link = item.querySelector('a');
        if (!link) return;

        link.addEventListener('click', (e) => {
            // On mobile, open submenu
            if (isMobile()) {
                e.preventDefault();
                openSubmenu(submenu, item);
                return;
            }

            // On desktop, toggle submenu
            e.preventDefault();

            // Close other submenus
            menuItems.forEach((otherItem) => {
                if (otherItem !== item && otherItem.classList.contains('is-active')) {
                    otherItem.classList.remove('is-active');
                }
            });

            // Toggle current submenu
            item.classList.toggle('is-active');
        });
    });

    // Close submenus when clicking outside (on backdrop for mobile)
    mainNav?.addEventListener('click', (e) => {
        if (!isMobile()) return;

        if (e.target === mainNav) {
            closeAllSubmenus();
        }
    });

    // Close desktop submenus when clicking outside
    document.addEventListener('click', (e) => {
        if (isMobile()) return;

        const clickedMenuItem = e.target.closest('.menu-item');
        const clickedSubmenu = e.target.closest('.sub-menu');
        const clickedNav = e.target.closest('.main-nav');

        // If click is outside the navigation area, close all submenus
        if (!clickedNav) {
            menuItems.forEach((item) => {
                item.classList.remove('is-active');
            });
        }
    });
}

setupMenu();

function setupDarkModeToggle() {
    const toggle = document.querySelector('.dark-mode-toggle');
    if (!toggle) return;
    if (toggle.dataset.themeInit) return;
    toggle.dataset.themeInit = '1';

    const osDark = window.matchMedia('(prefers-color-scheme: dark)');

    function isDarkActive() {
        const current = document.documentElement.getAttribute('data-theme');
        if (current === 'dark') return true;
        if (current === 'light') return false;
        const saved = localStorage.getItem('theme');
        if (saved === 'dark') return true;
        if (saved === 'light') return false;
        return osDark.matches;
    }

    function updateToggleLabel() {
        toggle.setAttribute('aria-label', isDarkActive() ? 'Switch to light mode' : 'Switch to dark mode');
    }

    updateToggleLabel();

    toggle.addEventListener('click', () => {
        if (isDarkActive()) {
            document.documentElement.setAttribute('data-theme', 'light');
            localStorage.setItem('theme', 'light');
        } else {
            document.documentElement.setAttribute('data-theme', 'dark');
            localStorage.setItem('theme', 'dark');
        }
        updateToggleLabel();
    });

    osDark.addEventListener('change', () => {
        if (!localStorage.getItem('theme')) {
            updateToggleLabel();
        }
    });
}

setupDarkModeToggle();

function attachGridReveal(overlay, container) {
    const EASE = 0.2;
    const HIDDEN = -400;
    const FADE_MS = 200;

    let targetX = HIDDEN, targetY = HIDDEN;
    let currentX = HIDDEN, currentY = HIDDEN;
    let rafId = null;
    let hideTimeoutId = null;

    container.style.setProperty('--grid-reveal-opacity', '0');

    function animate() {
        currentX += (targetX - currentX) * EASE;
        currentY += (targetY - currentY) * EASE;

        container.style.setProperty('--grid-mouse-x', `${currentX}px`);
        container.style.setProperty('--grid-mouse-y', `${currentY}px`);

        if (Math.abs(targetX - currentX) > 0.1 || Math.abs(targetY - currentY) > 0.1) {
            rafId = requestAnimationFrame(animate);
        } else {
            rafId = null;
        }
    }

    container.addEventListener('mousemove', (e) => {
        if (hideTimeoutId) {
            clearTimeout(hideTimeoutId);
            hideTimeoutId = null;
        }
        const rect = overlay.getBoundingClientRect();
        targetX = e.clientX - rect.left;
        targetY = e.clientY - rect.top;
        container.style.setProperty('--grid-reveal-opacity', '1');
        if (!rafId) rafId = requestAnimationFrame(animate);
    });

    container.addEventListener('mouseleave', () => {
        container.style.setProperty('--grid-reveal-opacity', '0');
        hideTimeoutId = setTimeout(() => {
            targetX = HIDDEN;
            targetY = HIDDEN;
            if (!rafId) rafId = requestAnimationFrame(animate);
            hideTimeoutId = null;
        }, FADE_MS);
    });
}

function setupGridReveal() {
    document.querySelectorAll('.grid-reveal-overlay').forEach((el) => {
        attachGridReveal(el, el.closest('.grid-reveal-container') ?? el.parentElement);
    });
}

setupGridReveal();

function setupImageGridLightbox() {
    document.querySelectorAll('.image-grid-block__images').forEach((galleryEl) => {
        if (!galleryEl.querySelector('.image-grid-block__lightbox-item')) return;
        if (galleryEl.dataset.lightboxReady === 'true') return;

        lightGallery(galleryEl, {
            selector: '.image-grid-block__lightbox-item',
            plugins: [lgVideo],
            download: false,
            mode: 'lg-fade',
            speed: 250,
            zoomFromOrigin: false,
            licenseKey: '0000-0000-000-0000',
        });

        galleryEl.dataset.lightboxReady = 'true';
    });
}

setupImageGridLightbox();

function setupVideoBlockPosters() {
    document.querySelectorAll('.video-block__media').forEach((root) => {
        if (root.dataset.videoPosterInit === 'true') return;
        const poster = root.querySelector('.video-block__poster');
        const iframe = root.querySelector('.video-block__iframe[data-src]');
        if (!poster || !iframe) return;
        root.dataset.videoPosterInit = 'true';

        function play() {
            const src = iframe.getAttribute('data-src');
            if (!src) return;
            root.classList.add('is-playing');
            iframe.removeAttribute('hidden');
            iframe.src = src;
            poster.hidden = true;
        }

        poster.addEventListener('click', play);
        poster.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                play();
            }
        });
    });
}

document.addEventListener('DOMContentLoaded', () => {
    setupImageGridInlineVideos();
    setupImageGridInlineVideoHoverPause();
    setupVideoBlockPosters();
    setupMailingListDialog();
});

function setupScrollHeader() {
    const header = document.querySelector('.main-header');
    if (!header || !document.body.classList.contains('home')) return;

    const DESKTOP_BREAKPOINT = 1020;
    const THRESHOLD = 5; // px of scroll needed before triggering

    let lastY = window.scrollY;

    window.addEventListener('scroll', () => {
        if (window.innerWidth < DESKTOP_BREAKPOINT) return;

        const currentY = window.scrollY;
        const delta = currentY - lastY;

        // Background only when away from the very top
        header.classList.toggle('is-scrolled', currentY > 0);

        if (Math.abs(delta) < THRESHOLD) return;

        if (delta > 0) {
            // Scrolling down — hide
            header.classList.add('header-hidden');
        } else {
            // Scrolling up — show
            header.classList.remove('header-hidden');
        }

        lastY = currentY;
    }, { passive: true });
}

setupScrollHeader();

function setupHomeHeroVideoFade() {
    const MOBILE_MAX_WIDTH = 767;
    const isMobile = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;

    document.querySelectorAll('.home-hero-block').forEach((section) => {
        const iframe = section.querySelector('.home-hero-block__vimeo-bg iframe');
        const preloader = section.querySelector('.home-hero-block__preloader');
        if (!iframe) return;

        const src = iframe.dataset.src;
        if (!src) return;

        let done = false;
        const markLoaded = () => {
            if (done) return;
            done = true;
            section.classList.add('is-video-loaded');
            preloader?.setAttribute('aria-hidden', 'true');
        };

        const startLoad = () => {
            if (iframe.src) return;
            iframe.src = src;
        };

        iframe.addEventListener('load', markLoaded, { once: true });
        window.setTimeout(markLoaded, 4000);

        if (isMobile) {
            const schedule =
                typeof window.requestIdleCallback === 'function'
                    ? (cb) => window.requestIdleCallback(cb, { timeout: 2000 })
                    : (cb) => window.setTimeout(cb, 1);

            schedule(startLoad);
        } else {
            startLoad();
        }
    });
}

setupHomeHeroVideoFade();

function setupMailingListDialog() {
    const dialog = document.querySelector('#mailing-list-dialog');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    const openers = document.querySelectorAll('[data-open-mailing-list-dialog]');
    const closers = dialog.querySelectorAll('[data-close-mailing-list-dialog]');

    dialog.addEventListener('close', () => {
        dialog.classList.remove('mailing-list-dialog--open');
        openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    });

    openers.forEach((btn) => {
        btn.addEventListener('click', () => {
            btn.setAttribute('aria-expanded', 'true');
            dialog.showModal();
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    dialog.classList.add('mailing-list-dialog--open');
                });
            });
        });
    });

    closers.forEach((btn) => {
        btn.addEventListener('click', () => dialog.close());
    });

    dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close();
    });
}

function resetMenuState() {
    // Reset hamburger menu state
    const hamburger = document.querySelector('.hamburger');
    if (hamburger) {
        hamburger.classList.remove('is-active');
        hamburger.setAttribute('aria-expanded', 'false');
    }

    // Remove menu-open class from body
    document.body.classList.remove('menu-open');
}
